import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loader2, MessageSquare, Bell, AlertTriangle, Star } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const KIND_META = {
  reminder60: { label: "60-min reminder", icon: Bell, cls: "bg-blue-600" },
  reminder30: { label: "30-min reminder", icon: Bell, cls: "bg-blue-600" },
  checkin: { label: "Check-in confirmation", icon: MessageSquare, cls: "bg-emerald-600" },
  autocharge: { label: "Auto-charge approved", icon: MessageSquare, cls: "bg-emerald-700" },
  autocharge_declined: { label: "Auto-charge declined", icon: AlertTriangle, cls: "bg-amber-600" },
  overstay: { label: "Overstay charge", icon: AlertTriangle, cls: "bg-red-600" },
  loyalty: { label: "Free night earned", icon: Star, cls: "bg-[#A0148C]" },
  generic: { label: "Notice", icon: MessageSquare, cls: "bg-slate-600" },
};

function statusClass(status) {
  if (status === "Overstayed") return "bg-red-600 hover:bg-red-600";
  if (status === "Checked out") return "bg-slate-700 hover:bg-slate-700";
  if (status === "Active") return "bg-emerald-600 hover:bg-emerald-600";
  return "bg-slate-700 hover:bg-slate-700";
}

export default function Activity() {
  const [logs, setLogs] = useState(null);
  const { selectedLot, isAll, currentLot } = useLocationScope();

  useEffect(() => {
    setLogs(null);
    const url = isAll ? "/sms-log" : `/sms-log?lotId=${selectedLot}`;
    api.get(url).then(({ data }) => setLogs(data));
  }, [selectedLot, isAll]);

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
          <MessageSquare className="h-6 w-6 text-[#A0148C]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Activity</h1>
          <p className="text-slate-400">
            {isAll ? "All locations" : currentLot?.name} — mocked SMS reminders, overstay &amp; loyalty notices.
          </p>
        </div>
      </div>

      {!logs ? (
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
        </div>
      ) : (
        <Card data-testid="activity-log" className="border-slate-800 bg-slate-900">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Time</TableHead>
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Driver</TableHead>
                <TableHead className="text-slate-400">Lot</TableHead>
                <TableHead className="text-slate-400">Message</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((m) => {
                const meta = KIND_META[m.kind] || KIND_META.generic;
                const Icon = meta.icon;
                return (
                  <TableRow
                    key={m.id}
                    data-testid={`activity-row-${m.id}`}
                    className="border-slate-800 hover:bg-slate-800/50"
                  >
                    <TableCell className="whitespace-nowrap font-data text-sm text-slate-300">
                      {fmt(m.sentAt)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`gap-1 ${meta.cls} hover:${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-slate-100">
                      {m.driverName || "—"}
                      <span className="block font-data text-xs font-normal text-slate-500">{m.to}</span>
                    </TableCell>
                    <TableCell className="text-slate-300">{m.lotName || "—"}</TableCell>
                    <TableCell className="max-w-md text-sm text-slate-400">{m.body}</TableCell>
                    <TableCell>
                      <Badge className={statusClass(m.renewalStatus)}>{m.renewalStatus}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {logs.length === 0 && (
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableCell colSpan={6} className="py-12 text-center text-slate-500">
                    No activity yet for this location.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
